import { useState } from 'react';
import { useFormikContext } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { ShowHideButton } from '@/components/show-hide';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';

import { useLoginMutation } from '@/query/auth.query';

import { useFormikError } from '@/lib/error';
import { storage } from '@/lib/storage';

import { LoginFormProvider } from '@/formik/auth.formik';
import { useAuthStore } from '@/store/auth.store';
import type { AuthResponse, ResetPasswordChallenge } from '@/types/auth.type';

const SubmitButton = ({ isPending }: { isPending: boolean }) => {
  const { isValid } = useFormikContext();

  return (
    <Button type="submit" className="w-full" disabled={isPending || !isValid}>
      {isPending ? 'Logging in...' : 'Login'}
    </Button>
  );
};

const Form = ({ toggle }) => {
  const { getFieldProps } = useFormikContext();
  const hasError = useFormikError();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <div className="grid gap-3">
        <Field aria-invalid={hasError('username')}>
          <RequiredLabel htmlFor="email">Email</RequiredLabel>
          <Input
            id="email"
            type="email"
            placeholder="m@example.com"
            {...getFieldProps('username')}
            aria-invalid={hasError('username')}
          />
          <ErrorField name="username" />
        </Field>
      </div>

      <div className="grid gap-3">
        <div className="flex items-center">
          <RequiredLabel htmlFor="password">Password</RequiredLabel>
          <button
            type="button"
            className="ml-auto text-sm underline-ohoffset-4 hover:underline bg-transparent p-0 text-primary"
            onClick={toggle}
          >
            Forgot your password?
          </button>
        </div>
        <div className="relative">
          <Field aria-invalid={hasError('password')}>
            <InputGroup>
              <InputGroupInput
                type={showPassword ? 'text' : 'password'}
                id="password"
                {...getFieldProps('password')}
                placeholder="Password"
                aria-invalid={hasError('password')}
              />
              <InputGroupAddon align="inline-end">
                <ShowHideButton
                  state={showPassword}
                  toggle={() => setShowPassword(prev => !prev)}
                />
              </InputGroupAddon>
            </InputGroup>
            <ErrorField name="password" />
          </Field>
        </div>
      </div>
    </>
  );
};

export function LoginForm({ toggle }) {
  const login = useAuthStore(state => state.login);
  const redirectPath = useAuthStore(state => state.redirectPath);

  const navigate = useNavigate();
  const location = useLocation();

  const loginMutation = useLoginMutation({
    suppressGlobalNotification: true,
    onSuccess: data => {
      if (data.type === 'RESET_PASSWORD_CHALLENGE') {
        const response = data as ResetPasswordChallenge;
        navigate('/reset-password', {
          state: {
            name: response.name,
            session: response.session,
            username: response.email,
          },
          replace: true,
        });
      } else if (data.type === 'LOGIN') {
        const response = data as AuthResponse;

        storage.setToken(response.access_token);
        login();
        const redirectTo =
          (location.state && location.state.from && location.state.from.pathname) || redirectPath;
        navigate(redirectTo, { replace: true });
      }
    },
  });

  return (
    <LoginFormProvider onSubmit={loginMutation.mutate}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Login to your account</h1>
        <p className="text-muted-foreground text-sm text-balance">
          Enter your email below to login to your account
        </p>
      </div>

      <div className="grid gap-6 py-6">
        <Form toggle={toggle} />

        {loginMutation.isError && (
          <p className="text-sm text-destructive">
            {loginMutation.error?.message || 'Login failed. Please try again.'}
          </p>
        )}
        <SubmitButton isPending={loginMutation.isPending} />
      </div>
    </LoginFormProvider>
  );
}
